import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the Turnstile challenge and hands back a short-lived token via
 * `onVerify`. The token MUST be verified server-side (see
 * `supabase/functions/submit-lead`) using the Turnstile *secret* key before it
 * is trusted — the site key rendered here is public and safe to ship.
 *
 * The site key is read from `VITE_TURNSTILE_SITE_KEY` so it can differ between
 * environments; it falls back to the production key when the env var is unset.
 */

// Public site key (safe to expose). Overridable per-environment via env var.
export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAADzUq09DpWj0k2Mz";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Shared, memoised loader so the Cloudflare script is only injected once even
// when several widgets mount on the same page.
let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Turnstile requires a browser environment"));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile script loaded without global"));
    };

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", finish);
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile(
  {
    onVerify,
    onExpire,
    onError,
    action,
    theme = "auto",
    // Cloudflare's widget sizing: "normal" (its default, a fixed 300px card),
    // "compact", or "flexible" — which fills the parent's width down to a 300px
    // floor. Flexible is what lines the widget up with a form's own fields
    // instead of leaving it visibly narrower than everything around it.
    size,
    className,
    style,
    verifiedLabel = "Verified human",
  },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Once the challenge is solved we collapse Cloudflare's own "Success!" card
  // (which looks bolted-on next to our UI) down to a small, quiet indicator.
  // The real widget stays mounted but hidden so the token and reset() keep
  // working exactly as before.
  const [verified, setVerified] = useState(false);

  // Keep the latest callbacks in refs so the one-time render effect never uses
  // a stale closure (the widget is rendered imperatively, outside React).
  const handlers = useRef({ onVerify, onExpire, onError });
  handlers.current = { onVerify, onExpire, onError };

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        setVerified(false);
        if (window.turnstile && widgetIdRef.current != null) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
      getResponse() {
        if (window.turnstile && widgetIdRef.current != null) {
          return window.turnstile.getResponse(widgetIdRef.current);
        }
        return null;
      },
    }),
    []
  );

  const notifyError = useCallback(() => {
    handlers.current.onError?.();
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || !turnstile) return;
        // Guard against a double render (e.g. React 18 StrictMode).
        if (widgetIdRef.current != null) return;

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme,
          ...(size ? { size } : {}),
          callback: (token) => {
            setVerified(true);
            handlers.current.onVerify?.(token);
          },
          "expired-callback": () => {
            setVerified(false);
            handlers.current.onExpire?.();
          },
          "error-callback": () => {
            setVerified(false);
            handlers.current.onError?.();
          },
        });
      })
      .catch(notifyError);

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already be gone; ignore.
        }
        widgetIdRef.current = null;
      }
    };
    // Render exactly once; live values are read from refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className} style={style}>
      {/* The Cloudflare widget renders here; hidden (not unmounted) once solved. */}
      <div ref={containerRef} style={verified ? { display: "none" } : undefined} />
      {verified && (
        <span
          role="status"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "'Archivo', sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            color: "#6E6A65",
            // `normal`, not `1`: with align-items:center the flex algorithm
            // centers the text's line box, and a line box tighter than the
            // font's glyph content lets the ink overflow downward and ride
            // ~0.5px high of the icon. `normal` contains the glyphs so the
            // label centers exactly against the check icon.
            lineHeight: "normal",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <circle cx="8" cy="8" r="8" fill="#2E7D5B" />
            <path
              d="M4.5 8.2l2.2 2.2 4.8-4.8"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {verifiedLabel}
        </span>
      )}
    </div>
  );
});

export default Turnstile;
